import type { DashRecap, DashRecapVariant } from './types';

/** A prompt the hero offers: its visible label and the text it seeds into the composer. */
export interface HeroPrompt { label: string; prompt: string }

/** The day's personalized variants, in rotation order. Previously stored batches carried their hero only
 *  at the top level; missing per-variant fields inherit it, while explicitly empty fields stay empty. */
export function recapVariants(recap: DashRecap | undefined): DashRecapVariant[] {
  const digest = recap?.enabled && recap.digest?.status === 'ready' ? recap.digest : undefined;
  if (!digest) return [];
  const batch = digest.recaps?.length ? digest.recaps : [digest];
  return batch.map((variant) => ({
    greeting: variant.greeting ?? digest.greeting,
    ask: variant.ask ?? digest.ask,
    pills: variant.pills ?? digest.pills,
    summary: variant.summary,
    suggestions: variant.suggestions,
  }));
}

/** The hero greeting: the agent-written one, else the reader's time-of-day line addressed by first name.
 *  `hour` is undefined until the browser's clock is known, and the greeting is then null. The nominative
 *  is used as-is: declining a name into the vocative cannot be done reliably for arbitrary names. */
export function heroGreeting(
  labels: { greetingMorning: string; greetingAfternoon: string; greetingEvening: string },
  hour: number | undefined,
  user: { name?: string | null; username: string } | undefined,
  variant: DashRecapVariant | undefined,
): string | null {
  const written = variant?.greeting?.trim();
  if (written) return written;
  if (hour === undefined) return null;
  const line = hour < 12 ? labels.greetingMorning : hour < 18 ? labels.greetingAfternoon : labels.greetingEvening;
  const firstName = user ? (user.name || user.username).trim().split(/\s+/)[0] : null;
  return `${line}${firstName ? `, ${firstName}` : ''}`;
}

/** The static prompt seeds that stand in when the agent wrote no pills for today. */
export function staticHeroPrompts(labels: {
  pillSummary: string; pillSummaryPrompt: string;
  pillPlan: string; pillPlanPrompt: string;
  pillAgent: string; pillAgentPrompt: string;
  pillFind: string; pillFindPrompt: string;
  pillCapabilities: string; pillCapabilitiesPrompt: string;
}) {
  return {
    summary: { label: labels.pillSummary, prompt: labels.pillSummaryPrompt },
    plan: { label: labels.pillPlan, prompt: labels.pillPlanPrompt },
    agent: { label: labels.pillAgent, prompt: labels.pillAgentPrompt },
    find: { label: labels.pillFind, prompt: labels.pillFindPrompt },
    capabilities: { label: labels.pillCapabilities, prompt: labels.pillCapabilitiesPrompt },
  } satisfies Record<string, HeroPrompt>;
}
